import * as React from 'react';
import HighchartsReact from 'highcharts-react-official';
import { PERIOD_BAR_HEIGHT_BUFFER, RenamingPeriodPayload } from 'activity-editor/support';
import { getPointData } from 'activity-editor/support/timeline-helpers';

export const RENAME_VIEW_WIDTH = 215; // width of 209 + 6px padding
export const RENAME_VIEW_OFFSET = 6;

export interface PeriodToolsContextShape {
    ganttRef: React.RefObject<HighchartsReact.RefObject>;
    sortedPeriods: Highcharts.GanttPointOptionsObject[];
    setSortedPeriods: (periods: Highcharts.GanttPointOptionsObject[]) => void;
    renamingPeriod?: RenamingPeriodPayload;
    setRenamingPeriod: (periodId?: string) => void;
}

const noProviderError = () => {
    console.error('No PeriodToolsContextProvider is present');
};

const PeriodToolsContext = React.createContext<PeriodToolsContextShape>({
    ganttRef: React.createRef<HighchartsReact.RefObject>(),
    sortedPeriods: [],
    setSortedPeriods: noProviderError,
    renamingPeriod: undefined,
    setRenamingPeriod: noProviderError,
});

export const PeriodToolsContextProvider: React.FunctionComponent = ({ children }) => {
    //Creating reference to chart
    const ganttRef = React.useRef<HighchartsReact.RefObject>(null);

    // Local state
    const [renamingPeriod, actualRenamingPeriod] = React.useState<RenamingPeriodPayload | undefined>();
    const [sortedPeriods, setSortedPeriods] = React.useState<Highcharts.GanttPointOptionsObject[]>([]);

    // Calculate position to decide to show start, end or center position
    const getViewPosition = (start: number, end: number, y: number) => {
        const ganttWidth = ganttRef?.current?.chart?.chartWidth || 0;
        const positionLeftStart = ganttRef?.current?.chart?.xAxis[0].toPixels(start, true) || 0;
        const positionLeftEnd = ganttRef?.current?.chart?.xAxis[0].toPixels(end, true) || 0;
        const fitStart = positionLeftStart > RENAME_VIEW_WIDTH;
        const fitEnd = ganttWidth - positionLeftEnd > RENAME_VIEW_WIDTH;
        const positionTop = (ganttRef?.current?.chart?.yAxis[0].toPixels(y, true) || 0) + PERIOD_BAR_HEIGHT_BUFFER;
        if (fitEnd) {
            return { left: positionLeftEnd + RENAME_VIEW_OFFSET, top: positionTop };
        }
        if (fitStart) {
            return { left: positionLeftStart - RENAME_VIEW_WIDTH, top: positionTop };
        }
        return { left: positionLeftStart + (positionLeftEnd - positionLeftStart) / 2, top: positionTop };
    };

    const setRenamingPeriod = (selectedPeriodId?: string) => {
        if (selectedPeriodId) {
            const selectedPeriodIndex = sortedPeriods.findIndex(
                (period) => period?.custom?.periodId && period?.custom?.periodId === selectedPeriodId,
            );
            const { start, end, y, name, periodId } = getPointData(sortedPeriods, selectedPeriodIndex);
            if (start && end && y && name && periodId) {
                actualRenamingPeriod({
                    name,
                    id: periodId,
                    position: getViewPosition(start, end, y),
                });
            }
        } else {
            actualRenamingPeriod(undefined);
        }
    };

    return (
        <PeriodToolsContext.Provider
            value={{
                ganttRef,
                sortedPeriods,
                setSortedPeriods,
                renamingPeriod,
                setRenamingPeriod,
            }}
        >
            {children}
        </PeriodToolsContext.Provider>
    );
};

export const usePeriodToolsContext = () => React.useContext(PeriodToolsContext);
